import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import NumpadInput from '../components/NumpadInput';

// A GS1 scan named a lot we don't know yet: register it with its first quantity
export default function AddLotPhase({ item, gs1Data, saving, error, canCreate, staffSignedIn, defaultLocationId, onConfirm, onCancel }) {
  const { t } = useTranslation(['inventory', 'common']);
  const [qty, setQty] = useState('1');
  const numQty = parseInt(qty, 10) || 0;
  const uom = item?.uom ?? '';

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pt: 1 }}>
        <IconButton onClick={onCancel} aria-label={t('common:buttons.cancel')} sx={{ width: 48, height: 48 }}>
          <BackIcon />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">{t('inventory:scanner.add_lot_title')}</Typography>
          <Typography variant="subtitle1" fontWeight={600} noWrap>{item?.name}</Typography>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
        {[
          gs1Data?.lot && t('inventory:scanner.lot_label', { lot: gs1Data.lot }),
          gs1Data?.expiry && t('inventory:scanner.expires', { date: gs1Data.expiry }),
        ].filter(Boolean).join(' · ')}
      </Typography>

      {!canCreate && (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            {t(staffSignedIn ? 'inventory:scanner.no_permission_for_lot' : 'inventory:scanner.staff_needed_for_lot')}
          </Alert>
          <Button variant="outlined" size="large" onClick={onCancel} fullWidth sx={{ minHeight: 52, fontSize: '1.05rem' }}>
            {t('inventory:scanner.scan_again')}
          </Button>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ m: 2, mb: 0 }}>{error}</Alert>}

      <Box sx={{ flexGrow: 1 }} />
      {canCreate && (
        <>
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="caption" color="text.secondary">{t('inventory:scanner.add_lot_quantity')}</Typography>
            <Typography sx={{ fontSize: 56, fontWeight: 700, lineHeight: 1.1 }}>
              {qty || '0'}
              <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1 }}>{uom}</Typography>
            </Typography>
          </Box>
          <Box sx={{ px: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <NumpadInput value={qty} onChange={setQty} disabled={saving} />
            <Button
              variant="contained"
              size="large"
              onClick={() => onConfirm(numQty, defaultLocationId || null)}
              disabled={!numQty || saving}
              sx={{ minHeight: 56, borderRadius: 2, fontSize: '1.1rem' }}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : t('inventory:scanner.add_lot_confirm', { count: numQty, uom })}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
